import { FormEvent, useEffect, useState } from "react";

import AppShell from "../components/AppShell";
import { CardSkeleton } from "../components/Skeleton";
import { listAgents, type AgentRow } from "../api/agents";
import {
  createDestination,
  createJob,
  listDestinations,
  listJobRuns,
  listJobs,
  restoreFromRun,
  runJobNow,
  type DestinationRow,
  type JobRow,
  type RunRow,
} from "../api/backups";

const POLL_INTERVAL_MS = 30_000;

const SCHEDULE_PRESETS: Array<{ label: string; cron: string }> = [
  { label: "Every night (11:00 PM)", cron: "0 23 * * *" },
  { label: "Every weekday morning (6:00 AM)", cron: "0 6 * * 1-5" },
  { label: "Every 6 hours", cron: "0 */6 * * *" },
];

export default function Backups() {
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [destinations, setDestinations] = useState<DestinationRow[] | null>(null);
  const [jobs, setJobs] = useState<JobRow[] | null>(null);
  const [recentRuns, setRecentRuns] = useState<Record<string, RunRow[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function refreshAll() {
    try {
      const [a, d, j] = await Promise.all([listAgents(), listDestinations(), listJobs()]);
      setAgents(a.results);
      setDestinations(d.results);
      setJobs(j.results);
      // Refresh per-job runs in parallel.
      const runs: Record<string, RunRow[]> = {};
      await Promise.all(
        j.results.map(async (job) => {
          try {
            const r = await listJobRuns(job.id);
            runs[job.id] = r.results;
          } catch {
            runs[job.id] = [];
          }
        }),
      );
      setRecentRuns(runs);
      setError(null);
    } catch (e) {
      setError(extractError(e));
    }
  }

  useEffect(() => {
    refreshAll();
    const id = setInterval(refreshAll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
     
  }, []);

  async function onRunNow(jobId: string) {
    setBusy(jobId);
    setError(null);
    try {
      await runJobNow(jobId);
      await refreshAll();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(null);
    }
  }

  async function onRestore(jobId: string, runId: string) {
    if (!window.confirm("Restore this snapshot? Files will be extracted to a 'Restored' folder on the host computer.")) return;
    setBusy(runId);
    setError(null);
    try {
      await restoreFromRun(jobId, runId);
      await refreshAll();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl space-y-10">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Backups</h1>
          <p className="mt-1 text-slate-600">
            Keep a copy of your office files on a USB drive or another computer.
          </p>
        </div>

        {error ? (
          <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}

        <Section title="Where backups go">
          <DestinationList rows={destinations} />
          <DestinationForm
            agents={agents ?? []}
            onCreated={refreshAll}
          />
        </Section>

        <Section title="What gets backed up">
          <JobList
            rows={jobs}
            destinations={destinations ?? []}
            recentRuns={recentRuns}
            busy={busy}
            onRunNow={onRunNow}
            onRestore={onRestore}
          />
          <JobForm
            agents={agents ?? []}
            destinations={destinations ?? []}
            onCreated={refreshAll}
          />
        </Section>
      </div>
    </AppShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

function DestinationList({ rows }: { rows: DestinationRow[] | null }) {
  if (rows === null) return <CardSkeleton count={2} />;
  if (rows.length === 0)
    return <p className="text-sm text-slate-500">No destinations yet. Add one below.</p>;
  return (
    <ul className="space-y-2">
      {rows.map((d) => (
        <li key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-base font-semibold text-slate-900">{d.label}</div>
              <div className="mt-1 break-all text-sm text-slate-500">
                {d.type.toUpperCase()} on{" "}
                <span className="font-medium">{d.host_agent_label || d.host_agent_hostname}</span>
                {" · "}
                <span className="font-mono">{d.path_or_uri}</span>
              </div>
              {d.last_init_error ? (
                <div className="mt-1 text-xs text-red-700">{d.last_init_error}</div>
              ) : null}
            </div>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                d.restic_repo_initialized
                  ? "bg-emerald-100 text-emerald-900"
                  : "bg-amber-100 text-amber-900"
              }`}
            >
              {d.restic_repo_initialized ? "Ready" : "Setting up…"}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DestinationForm({ agents, onCreated }: { agents: AgentRow[]; onCreated: () => void }) {
  const onlineAgents = agents.filter((a) => a.status === "online");
  const [hostId, setHostId] = useState("");
  const [path, setPath] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (onlineAgents.length > 0 && !hostId) setHostId(onlineAgents[0].id);
  }, [onlineAgents, hostId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await createDestination({
        type: "usb",
        host_agent_id: hostId,
        path,
        label,
      });
      setPath("");
      setLabel("");
      onCreated();
    } catch (ex) {
      setErr(extractError(ex));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-dashed border-slate-300 bg-white p-5"
    >
      <div className="text-sm font-medium text-slate-700">Add a destination</div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field
          label="Which computer has the drive?"
          render={
            <select
              aria-label="Which computer has the drive?"
              value={hostId}
              onChange={(e) => setHostId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              required
            >
              {onlineAgents.length === 0 ? <option value="">No online computers</option> : null}
              {onlineAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label || a.hostname}
                </option>
              ))}
            </select>
          }
        />
        <Field
          label="Path on that computer"
          render={
            <input
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder={String.raw`D:\KraetdNetBackups`}
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 font-mono text-sm"
              required
            />
          }
        />
      </div>
      <div className="mt-3">
        <Field
          label="What should we call it?"
          render={
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Backup drive on Reception PC"
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              required
            />
          }
        />
      </div>
      {err ? <p className="mt-2 text-sm text-red-700">{err}</p> : null}
      <button
        type="submit"
        disabled={submitting || !hostId || !path || !label}
        className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {submitting ? "Setting up…" : "Add destination"}
      </button>
    </form>
  );
}

function JobList({
  rows,
  destinations,
  recentRuns,
  busy,
  onRunNow,
  onRestore,
}: {
  rows: JobRow[] | null;
  destinations: DestinationRow[];
  recentRuns: Record<string, RunRow[]>;
  busy: string | null;
  onRunNow: (id: string) => void;
  onRestore: (jobId: string, runId: string) => void;
}) {
  if (rows === null) return <CardSkeleton count={2} />;
  if (rows.length === 0)
    return <p className="text-sm text-slate-500">No backup jobs yet. Add one below.</p>;
  return (
    <ul className="space-y-3">
      {rows.map((job) => {
        const dest = destinations.find((d) => d.id === job.destination);
        const runs = recentRuns[job.id] ?? [];
        const latest = runs[0];
        return (
          <li key={job.id} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-900">
                  {job.label || `Backup of ${job.source_agent_hostname}`}
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  From <span className="font-medium">{job.source_agent_label || job.source_agent_hostname}</span>
                  {" → "}
                  <span className="font-medium">{dest?.label ?? "?"}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Schedule: <span className="font-mono">{job.schedule_cron}</span>
                  {job.next_run_at ? <> · Next run: {formatDateTime(job.next_run_at)}</> : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRunNow(job.id)}
                disabled={busy === job.id || !dest?.restic_repo_initialized}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                title={!dest?.restic_repo_initialized ? "Destination still setting up" : ""}
              >
                {busy === job.id ? "Working…" : "Run now"}
              </button>
            </div>

            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Recent runs
              </div>
              {runs.length === 0 ? (
                <p className="mt-1 text-sm text-slate-500">None yet.</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {runs.slice(0, 5).map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-3 text-xs"
                    >
                      <div>
                        <span className="font-medium">{formatDateTime(r.started_at)}</span>
                        {" · "}
                        <RunBadge status={r.status} />
                        {r.status === "succeeded" && r.files_count !== null ? (
                          <> · {r.files_count} files · {formatBytes(r.bytes_added ?? 0)} new</>
                        ) : null}
                        {r.error_message ? (
                          <span className="ml-2 text-red-700">{r.error_message}</span>
                        ) : null}
                      </div>
                      {r.status === "succeeded" ? (
                        <button
                          type="button"
                          onClick={() => onRestore(job.id, r.id)}
                          disabled={busy === r.id}
                          className="text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
                        >
                          Restore
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              {latest ? null : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function JobForm({
  agents,
  destinations,
  onCreated,
}: {
  agents: AgentRow[];
  destinations: DestinationRow[];
  onCreated: () => void;
}) {
  const onlineAgents = agents.filter((a) => a.status === "online");
  const initializedDests = destinations.filter((d) => d.restic_repo_initialized);
  const [destId, setDestId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [paths, setPaths] = useState("");
  const [scheduleCron, setScheduleCron] = useState(SCHEDULE_PRESETS[0].cron);
  const [customCron, setCustomCron] = useState(false);
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (initializedDests.length > 0 && !destId) setDestId(initializedDests[0].id);
    if (onlineAgents.length > 0 && !sourceId) setSourceId(onlineAgents[0].id);
  }, [initializedDests, destId, onlineAgents, sourceId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const pathLines = paths
        .split("\n")
        .map((p) => p.trim())
        .filter(Boolean);
      await createJob({
        destination_id: destId,
        source_agent_id: sourceId,
        paths: pathLines,
        schedule_cron: scheduleCron,
        label,
      });
      setPaths("");
      setLabel("");
      onCreated();
    } catch (ex) {
      setErr(extractError(ex));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-dashed border-slate-300 bg-white p-5"
    >
      <div className="text-sm font-medium text-slate-700">Add a backup job</div>
      {initializedDests.length === 0 ? (
        <p className="mt-2 text-sm text-amber-900">
          Add a destination first. Once it shows "Ready", you can create a backup job here.
        </p>
      ) : null}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field
          label="Where do they go?"
          render={
            <select
              aria-label="Where do they go?"
              value={destId}
              onChange={(e) => setDestId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              disabled={initializedDests.length === 0}
              required
            >
              {initializedDests.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          }
        />
        <Field
          label="Which computer to back up?"
          render={
            <select
              aria-label="Which computer to back up?"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              required
            >
              {onlineAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label || a.hostname}
                </option>
              ))}
            </select>
          }
        />
      </div>
      <div className="mt-3">
        <Field
          label="Folders to back up (one per line)"
          render={
            <textarea
              value={paths}
              onChange={(e) => setPaths(e.target.value)}
              placeholder={String.raw`C:\Important
C:\Users\Public\Documents`}
              className="mt-1 h-24 w-full rounded-lg border border-slate-300 p-2 font-mono text-xs"
              required
            />
          }
        />
      </div>
      <div className="mt-3">
        <Field
          label="When should it run?"
          render={
            <div>
              <select
                aria-label="When should it run?"
                value={customCron ? "__custom" : scheduleCron}
                onChange={(e) => {
                  if (e.target.value === "__custom") {
                    setCustomCron(true);
                  } else {
                    setCustomCron(false);
                    setScheduleCron(e.target.value);
                  }
                }}
                className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              >
                {SCHEDULE_PRESETS.map((p) => (
                  <option key={p.cron} value={p.cron}>
                    {p.label}
                  </option>
                ))}
                <option value="__custom">Custom (advanced)</option>
              </select>
              {customCron ? (
                <input
                  value={scheduleCron}
                  onChange={(e) => setScheduleCron(e.target.value)}
                  placeholder="0 23 * * *"
                  className="mt-2 w-full rounded-lg border border-slate-300 p-2 font-mono text-sm"
                />
              ) : null}
            </div>
          }
        />
      </div>
      <div className="mt-3">
        <Field
          label="Name this job (optional)"
          render={
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Nightly backup of accounting PC"
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
            />
          }
        />
      </div>
      {err ? <p className="mt-2 text-sm text-red-700">{err}</p> : null}
      <button
        type="submit"
        disabled={
          submitting ||
          initializedDests.length === 0 ||
          !destId ||
          !sourceId ||
          !paths.trim()
        }
        className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {submitting ? "Creating…" : "Add backup job"}
      </button>
    </form>
  );
}

function Field({ label, render }: { label: string; render: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {render}
    </label>
  );
}

function RunBadge({ status }: { status: RunRow["status"] }) {
  const map: Record<RunRow["status"], { label: string; cls: string }> = {
    running: { label: "Running…", cls: "bg-amber-100 text-amber-900" },
    succeeded: { label: "Done", cls: "bg-emerald-100 text-emerald-900" },
    failed: { label: "Failed", cls: "bg-red-100 text-red-900" },
    interrupted: { label: "Stopped", cls: "bg-slate-200 text-slate-700" },
  };
  const m = map[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${m.cls}`}>{m.label}</span>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return "Something went wrong. Please try again.";
}
